import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Send, Sparkles, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LangContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectAI({ projectId, project }) {
    const { user, session } = useAuth();
    const { lang, t } = useLang();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [quota, setQuota] = useState(null);

    const refreshQuota = React.useCallback(() => {
        if (!session) return;
        axios.get(`${API}/ai/quota`, { headers: { Authorization: `Bearer ${session.access_token}` } })
            .then(r => setQuota(r.data)).catch(() => { });
    }, [session]);

    useEffect(() => { refreshQuota(); }, [refreshQuota]);
    const [busy, setBusy] = useState(false);
    const scrollRef = useRef(null);

    const sessionId = `${user?.id}-${projectId || 'global'}`;

    useEffect(() => {
        if (!user) return;
        axios.get(`${API}/ai/history/${sessionId}`).then((r) => setMessages(r.data || [])).catch(() => { });
    }, [sessionId, user]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }, [messages]);

    const send = async (text) => {
        const msg = (text || input).trim();
        if (!msg || busy) return;
        setInput(''); setBusy(true);
        setMessages((m) => [...m, { role: 'user', content: msg, created_at: new Date().toISOString() }]);
        try {
            const context = project ? `Projet: ${project.name} (${project.industry || '—'}, ${project.country || '—'}) — Statut ${project.status}, Avancement ${project.progress || 0}%, Budget ${project.budget_total || 0} ${project.currency}.` : null;
            const { data } = await axios.post(`${API}/ai/chat`, {
                session_id: sessionId, message: msg, language: lang, context,
            }, { headers: { Authorization: `Bearer ${session?.access_token}` } });
            setMessages((m) => [...m, { role: 'assistant', content: data.reply, created_at: data.created_at }]);
            refreshQuota();
        } catch (err) {
            setMessages((m) => [...m, { role: 'assistant', content: `Erreur: ${err.response?.data?.detail || err.message}`, created_at: new Date().toISOString() }]);
        }
        setBusy(false);
    };

    const suggestions = [
        t('ai.sug_1'), t('ai.sug_2'), t('ai.sug_3'), t('ai.sug_4'),
    ];

    return (
        <div className="grid lg:grid-cols-12 gap-6">
            <aside className="lg:col-span-3 card-soft p-5 h-fit">
                <div className="flex items-center gap-2 mb-4">
                    <Sparkles size={16} className="text-primary" /> <h3 className="font-heading text-lg">{t('ai.suggestions_title')}</h3>
                </div>
                {quota && (
                    <div className="mb-4 text-xs rounded-lg border border-border bg-surface-muted/50 px-3 py-2 flex items-center justify-between"
                        data-testid="ai-quota-counter">
                        <span className="text-muted-foreground">IA ce mois-ci</span>
                        <span className={`font-mono font-semibold ${quota.limit !== null && quota.remaining <= 5 ? 'text-destructive' : 'text-foreground/85'}`}>
                            {quota.limit === null ? `${quota.used} / ∞` : `${quota.used} / ${quota.limit}`}
                        </span>
                    </div>
                )}
                <div className="space-y-2">
                    {suggestions.map((s) => (
                        <button
                            key={s} onClick={() => send(s)}
                            className="w-full text-left text-sm px-3 py-2 rounded-md border border-border hover:bg-surface-muted hover:border-primary/40 transition-colors"
                            data-testid={`ai-suggestion-${s.slice(0, 20)}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </aside>

            <div className="lg:col-span-9 card-soft flex flex-col h-[calc(100vh-280px)] min-h-[500px]">
                <div className="px-6 py-4 border-b border-border">
                    <h3 className="font-heading text-xl">{t('ai.title')}</h3>
                </div>
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="ai-chat">
                    {messages.length === 0 && (
                        <div className="text-center text-foreground/60 py-12">
                            <Sparkles size={28} className="mx-auto text-primary mb-3" strokeWidth={1.4} />
                            <p>Posez une question, demandez une synthèse, ou explorez les suggestions.</p>
                        </div>
                    )}
                    {messages.map((m, i) => (
                        <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div
                                className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-surface-muted border border-border text-foreground'}`}
                                data-testid={`ai-msg-${m.role}-${i}`}
                            >
                                {m.content}
                            </div>
                        </div>
                    ))}
                    {busy && (
                        <div className="flex justify-start">
                            <div className="rounded-2xl px-4 py-3 bg-surface-muted text-foreground/70 text-sm inline-flex items-center gap-2">
                                <Loader2 size={14} className="animate-spin" /> {t('ai.thinking')}
                            </div>
                        </div>
                    )}
                </div>
                <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-border p-4 flex gap-3 items-end">
                    <Textarea
                        rows={2} value={input} onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                        placeholder={t('ai.placeholder')}
                        className="resize-none"
                        data-testid="ai-input"
                    />
                    <Button type="submit" disabled={busy || !input.trim()} className="bg-primary hover:bg-primary-hover h-11 px-5" data-testid="ai-send">
                        <Send size={14} className="mr-1.5" /> {t('ai.send')}
                    </Button>
                </form>
            </div>
        </div>
    );
}
